'use client';

import React, { useState, useEffect, useRef, useMemo, useCallback, useSyncExternalStore } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Search, X, Sparkles, HelpCircle, Settings, LogOut } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { getImageUrl } from '@/lib/api';
import NotificationSystem from '@/components/NotificationSystem';
import {
  AuraDashboardIcon,
  AuraContactsIcon,
  AuraPropertiesIcon,
  AuraDevelopmentsIcon,
  AuraPipelineIcon,
  AuraCalendarIcon,
  AuraShiftsIcon,
  AuraUsersIcon,
  AuraSettingsIcon,
  AuraAgencyIcon,
  AuraHelpIcon,
  AuraWebsiteIcon,
  AuraMarketingIcon,
  AuraToolsIcon,
  AuraDocumentsIcon,
  AuraWorkflowsIcon,
  AuraReportsIcon,
  AuraMessagingIcon,
  AuraEmailIcon,
  AuraAiIcon,
  AuraPymesIcon,
  AuraSubscriptionsIcon,
  AuraSecurityIcon,
  AuraCommissionsIcon,
  AuraAutomationsIcon
} from './AuraAbstractIcons';

const DEFAULT_ASSISTANT_NAME = 'Baly';

/** Snapshot compartido del nombre del asistente para useSyncExternalStore. */
let assistantNameSnapshot = DEFAULT_ASSISTANT_NAME;
const assistantNameListeners = new Set();

/** Lee el nombre del asistente desde la caché local con fallback seguro. */
function readAssistantNameFromCache() {
  try {
    const cached = window.localStorage.getItem('ai_assistant_config');
    if (!cached) return DEFAULT_ASSISTANT_NAME;
    const parsed = JSON.parse(cached);
    return parsed.agent_name || DEFAULT_ASSISTANT_NAME;
  } catch {
    return DEFAULT_ASSISTANT_NAME;
  }
}

/** Suscribe componentes a los cambios de identidad del asistente. */
function subscribeToAssistantName(listener) {
  // Refresca desde la caché al montar o remontar el componente.
  assistantNameSnapshot = readAssistantNameFromCache();
  assistantNameListeners.add(listener);

  const handleIdentityUpdated = (event) => {
    const nextName = event?.detail?.agent_name;
    if (!nextName || nextName === assistantNameSnapshot) return;
    assistantNameSnapshot = nextName;
    assistantNameListeners.forEach((notify) => notify());
  };

  window.addEventListener('ai-agent-identity-updated', handleIdentityUpdated);
  return () => {
    assistantNameListeners.delete(listener);
    window.removeEventListener('ai-agent-identity-updated', handleIdentityUpdated);
  };
}

/** Snapshot estable para el render en cliente. */
function getAssistantNameSnapshot() {
  return assistantNameSnapshot;
}

/** Snapshot seguro para SSR e hidratación inicial. */
function getAssistantNameServerSnapshot() {
  return DEFAULT_ASSISTANT_NAME;
}

/** Hook que expone el nombre del asistente sincronizado con localStorage y eventos. */
function useAssistantName() {
  return useSyncExternalStore(
    subscribeToAssistantName,
    getAssistantNameSnapshot,
    getAssistantNameServerSnapshot
  );
}

export default function OdooAppLauncher({
  isOpen = false,
  onClose,
  isMasterAdmin = false,
  setSelectedWorkplace,
  isStandalone = false
}) {
  const router = useRouter();
  const { user, logout } = useAuth() || {};
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);

  const handleClose = useCallback(() => {
    setSearchQuery('');
    if (onClose) onClose();
  }, [onClose]);

  const assistantName = useAssistantName();

  // Identidad del espacio de trabajo: la agencia es la protagonista, REGE firma.
  const agencyName = user?.agency?.name?.trim() || 'REGE';
  const agencyLogo = user?.agency?.logo_url ? getImageUrl(user.agency.logo_url) : null;
  const agencyInitials = agencyName
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();

  const handleToggleBalyAssistant = () => {
    window.dispatchEvent(new CustomEvent('toggle-baly-assistant'));
  };

  // Habilitar atajo ESC al abrir modal
  useEffect(() => {
    if (isStandalone || !isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, isStandalone, handleClose]);

  // Lista exacta de módulos solicitados por el usuario con iconografía vectorial abstracta original
  const allApps = useMemo(() => {
    const role = (user?.role || '').toLowerCase();
    const isAdmin = role === 'admin' || role === 'superadmin' || isMasterAdmin;

    const realEstateApps = [
      {
        id: 'dashboard',
        name: 'Dashboard',
        href: '/dashboard/analytics',
        workplace: 'sales',
        icon: AuraDashboardIcon
      },
      {
        id: 'contacts',
        name: 'Contactos',
        href: '/dashboard/contacts',
        workplace: 'sales',
        icon: AuraContactsIcon
      },
      {
        id: 'properties',
        name: 'Inmuebles',
        href: '/dashboard/properties',
        workplace: 'sales',
        icon: AuraPropertiesIcon
      },
      {
        id: 'developments',
        name: 'Proyectos & Desarrollos',
        href: '/dashboard/developments',
        workplace: 'sales',
        icon: AuraDevelopmentsIcon
      },
      {
        id: 'pipeline',
        name: 'Pipeline Leads',
        href: '/dashboard/leads',
        workplace: 'sales',
        icon: AuraPipelineIcon
      },
      {
        id: 'calendar',
        name: 'Calendario & Citas',
        href: '/dashboard/calendar',
        workplace: 'sales',
        icon: AuraCalendarIcon
      },
      {
        id: 'shifts',
        name: 'Turnos por Proyecto',
        href: '/dashboard/calendar/shifts',
        workplace: 'sales',
        icon: AuraShiftsIcon
      },
      {
        id: 'marketing',
        name: 'Marketing AI',
        href: '/dashboard/marketing',
        workplace: 'marketing',
        icon: AuraMarketingIcon
      },
      {
        id: 'baly-ai',
        name: 'Baly Copilot',
        href: '/dashboard/marketing',
        workplace: 'marketing',
        icon: AuraAiIcon
      },
      {
        id: 'messaging',
        name: 'WhatsApp',
        href: '/dashboard/messaging/conversations',
        workplace: 'sales',
        icon: AuraMessagingIcon,
        keywords: ['mensajería', 'chat', 'conversaciones', 'inbox']
      },
      {
        id: 'email',
        name: 'Email',
        href: '/dashboard/messaging/conversations?channel=email',
        workplace: 'sales',
        icon: AuraEmailIcon,
        keywords: ['email', 'correo', 'correos', 'bandeja', 'inbox', 'mensajería']
      },
      {
        id: 'tools',
        name: 'Herramientas',
        href: '/dashboard/tools',
        workplace: 'operations',
        icon: AuraToolsIcon
      },
      {
        id: 'workflows',
        name: 'Expedientes',
        href: '/dashboard/workflows',
        workplace: 'operations',
        icon: AuraWorkflowsIcon
      },
      {
        id: 'automations',
        name: 'Automatizaciones',
        href: '/dashboard/automations',
        workplace: 'operations',
        icon: AuraAutomationsIcon,
        keywords: ['automatizaciones', 'flujos', 'n8n', 'triggers', 'disparadores', 'acciones', 'workflows']
      },
      {
        id: 'documents',
        name: 'Documentos',
        href: '/dashboard/documents',
        workplace: 'operations',
        icon: AuraDocumentsIcon
      },
      {
        id: 'reports',
        name: 'Reportes',
        href: '/dashboard/reports',
        workplace: 'operations',
        icon: AuraReportsIcon
      },
      {
        id: 'commissions',
        name: 'Cierres & Comisiones',
        href: '/dashboard/reports/commissions',
        workplace: 'operations',
        icon: AuraCommissionsIcon
      },
      {
        id: 'website',
        name: 'Sitio Web',
        href: '/dashboard/website',
        workplace: 'marketing',
        icon: AuraWebsiteIcon
      },
      {
        id: 'users',
        name: 'Gestión de Usuarios',
        href: '/dashboard/users',
        workplace: 'settings',
        icon: AuraUsersIcon
      },
      {
        id: 'settings',
        name: 'Ajustes Generales',
        href: '/dashboard/settings',
        workplace: 'settings',
        icon: AuraSettingsIcon,
        keywords: ['ajustes', 'configuración', 'sistema', 'general', 'preferencias']
      },
      ...(isAdmin ? [
        {
          id: 'agency',
          name: 'Configuración de Empresa',
          href: '/dashboard/settings/agency',
          workplace: 'settings',
          icon: AuraAgencyIcon,
          keywords: ['empresa', 'agencia', 'configuración de empresa', 'inmobiliaria', 'logo', 'datos', 'fiscal', 'perfil empresa', 'identidad', 'corporativo']
        }
      ] : []),
      {
        id: 'help',
        name: 'Centro de Ayuda',
        href: '/dashboard/help',
        workplace: 'settings',
        icon: AuraHelpIcon,
        keywords: ['ayuda', 'soporte', 'manual', 'tickets', 'contacto']
      }
    ];

    const masterSaaSApps = isMasterAdmin ? [
      {
        id: 'master-dash',
        name: 'Dashboard SaaS',
        href: '/dashboard/master',
        icon: AuraDashboardIcon,
        workplace: 'master'
      },
      {
        id: 'pymes',
        name: 'Pymes & Clientes',
        href: '/dashboard/pymes',
        icon: AuraPymesIcon,
        workplace: 'master'
      },
      {
        id: 'subscriptions',
        name: 'Suscripciones',
        href: '/dashboard/master/plans',
        icon: AuraSubscriptionsIcon,
        workplace: 'master'
      },
      {
        id: 'baly-builder',
        name: 'Baly Builder',
        href: '/dashboard/workflows/builder',
        icon: AuraAiIcon,
        workplace: 'master'
      },
      {
        id: 'audit',
        name: 'Auditoría ISO',
        href: '/dashboard/master/audit',
        icon: AuraSecurityIcon,
        workplace: 'master'
      }
    ] : [];

    return [...realEstateApps, ...masterSaaSApps];
  }, [isMasterAdmin, user?.role]);

  // Filtrado reactivo en tiempo real por búsqueda
  const filteredApps = useMemo(() => {
    if (!searchQuery.trim()) return allApps;
    const normalize = (str) => 
      str ? str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim() : "";
    const q = normalize(searchQuery);
    return allApps.filter((app) => 
      normalize(app.name).includes(q) ||
      (app.keywords && app.keywords.some((k) => normalize(k).includes(q)))
    );
  }, [allApps, searchQuery]);

  const handleLaunchApp = (app) => {
    if (app.workplace && setSelectedWorkplace) {
      setSelectedWorkplace(app.workplace);
    }
    handleClose();
    router.push(app.href);
  };

  if (!isStandalone && !isOpen) return null;

  return (
    <div className={isStandalone ? "w-full min-h-screen flex flex-col justify-between pt-3 pb-6 px-3 sm:px-6 animate-in fade-in duration-200" : "fixed inset-0 z-[100] flex flex-col justify-between overflow-y-auto animate-in fade-in duration-200"}>
      {/* Fondo degradado suave estilo Odoo (lavanda pastel y blanco nieve, sin patrones ruidosos) */}
      {!isStandalone && (
        <div 
          className="fixed inset-0 -z-10 bg-gradient-to-br from-[#ebe6f2] via-[#f5f3f8] to-[#e4e0ee]"
          onClick={handleClose}
        />
      )}

      {/* Header Superior Minimalista Estilo Odoo 20 */}
      <header className="w-full px-2 sm:px-6 pt-2 pb-2 flex flex-col items-center gap-4">
        {/* Barra Superior Completa: Badge a la izquierda, Iconos de Usuario en la esquina superior derecha */}
        <div className="w-full flex items-center justify-between">
          <div className="flex min-w-0 items-center gap-3">
            {/* Identidad de la agencia (dueña del espacio de trabajo) */}
            <div className="flex min-w-0 items-center gap-2.5">
              {agencyLogo ? (
                <img
                  src={agencyLogo}
                  alt={`${agencyName} Logo`}
                  draggable={false}
                  className="h-8 w-8 shrink-0 rounded-lg border border-slate-200 bg-white object-contain p-0.5 shadow-2xs"
                />
              ) : (
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#6A1B9A] to-[#E040FB] text-[11px] font-black text-white shadow-2xs">
                  {agencyInitials || 'R'}
                </div>
              )}
              <div className="hidden min-w-0 sm:block">
                <p className="truncate text-[13px] font-extrabold leading-tight tracking-tight text-slate-900">
                  {agencyName}
                </p>
                <p className="truncate text-[9px] font-bold uppercase tracking-[0.16em] text-slate-400">
                  Espacio de trabajo
                </p>
              </div>
            </div>

            {/* Firma de la plataforma */}
            <div className="hidden h-6 w-px bg-slate-300/70 md:block" />
            <img
              src="/brand/rege-lockup-horizontal-ink.svg"
              alt="REGE — Real Estate Growth Engine"
              draggable={false}
              className="hidden h-[18px] w-auto shrink-0 opacity-55 md:block"
            />
          </div>

          {/* Barra con datos de usuario e iconos tipo Odoo 20 sobre fondo blanco */}
          <div className="flex items-center gap-1.5 sm:gap-2.5">
            {/* Botón Copilot */}
            <button
              onClick={handleToggleBalyAssistant}
              type="button"
              className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl bg-white/80 hover:bg-white text-slate-700 hover:text-slate-900 border border-slate-200/80 shadow-2xs transition-all cursor-pointer group"
              title={`Abrir ${assistantName} AI Copilot`}
            >
              <Sparkles size={14} className="text-amber-500 group-hover:scale-110 transition-transform animate-pulse" />
              <span className="font-extrabold text-xs tracking-tight text-slate-900">ai</span>
              <span className="text-xs font-medium text-slate-600">{assistantName}</span>
            </button>

            {/* Notificaciones */}
            <div className="flex items-center text-slate-600">
              <NotificationSystem
                buttonClassName="relative p-2 text-slate-600 hover:text-slate-900 hover:bg-white/80 rounded-lg transition-colors border border-transparent hover:border-slate-200/60"
                iconClassName="w-5 h-5"
              />
            </div>

            {/* Centro de Ayuda */}
            <Link
              href="/dashboard/help"
              onClick={handleClose}
              className="p-2 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-white/80 transition-colors cursor-pointer border border-transparent hover:border-slate-200/60"
              title="Centro de Ayuda"
            >
              <HelpCircle size={19} />
            </Link>

            {/* Ajustes Generales */}
            <Link
              href="/dashboard/settings"
              onClick={handleClose}
              className="p-2 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-white/80 transition-colors cursor-pointer border border-transparent hover:border-slate-200/60"
              title="Ajustes Generales"
            >
              <Settings size={19} />
            </Link>

            {/* Separador Vertical */}
            <div className="h-5 w-px bg-slate-300/80 mx-0.5 sm:mx-1" />

            {/* Perfil de Usuario */}
            <Link
              href="/dashboard/profile"
              onClick={handleClose}
              className="flex items-center gap-2 px-1.5 py-1 rounded-xl hover:bg-white/80 border border-transparent hover:border-slate-200/60 transition-all cursor-pointer group"
              title="Mi Perfil"
            >
              <div className="w-8 h-8 rounded-full overflow-hidden border border-slate-200/90 bg-white shadow-2xs shrink-0 ring-1 ring-slate-100">
                <img
                  src={user?.profile_picture_url ? getImageUrl(user.profile_picture_url) : 'https://www.gravatar.com/avatar/000?d=mp'}
                  className="w-full h-full object-cover"
                  alt="Usuario"
                  onError={(e) => { e.target.src = "https://www.gravatar.com/avatar/000?d=mp"; }}
                />
              </div>
              <div className="hidden sm:flex flex-col text-left">
                <span className="text-xs font-bold text-slate-800 group-hover:text-blue-600 transition-colors leading-tight truncate max-w-[130px]">
                  {user?.full_name || user?.username || 'Usuario'}
                </span>
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                  {user?.role || 'ADMIN'}
                </span>
              </div>
            </Link>

            {/* Cerrar Sesión */}
            <button
              onClick={() => {
                if (logout) logout();
              }}
              type="button"
              className="p-2 text-slate-500 hover:text-rose-600 hover:bg-rose-50/80 rounded-lg transition-colors cursor-pointer"
              title="Cerrar Sesión"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>

        {/* Buscador Central Minimalista (Pill) */}
        <div className="w-full max-w-md mx-auto relative pt-1">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
            <Search size={16} />
          </div>
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && filteredApps.length > 0) {
                e.preventDefault();
                const target = filteredApps[0];
                if (target.workplace && setSelectedWorkplace) {
                  setSelectedWorkplace(target.workplace);
                }
                handleClose();
                router.push(target.href);
              }
            }}
            placeholder="Buscar aplicación..."
            className="w-full pl-10 pr-10 py-2.5 bg-white/85 backdrop-blur-md text-slate-800 text-xs sm:text-sm font-medium rounded-full border border-slate-200/80 shadow-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-slate-400"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 cursor-pointer"
            >
              <X size={15} />
            </button>
          )}
        </div>
      </header>

      {/* Cuadrícula Principal con Iconografía Abstracta 100% Original */}
      <main className="w-full max-w-5xl mx-auto px-6 py-6 flex-1 flex flex-col justify-center">
        {/* Título de sección: la marca vive en el encabezado, no en el título */}
        <div className="mb-7 text-center">
          <h1 className="text-[20px] font-extrabold tracking-tight text-slate-900 sm:text-[22px]">
            Apps &amp; Módulos
          </h1>
          <p className="mt-1 text-[11px] font-medium text-slate-500 sm:text-xs">
            Todo tu ecosistema inmobiliario en un solo lugar
          </p>
        </div>

        {filteredApps.length === 0 ? (
          <div className="text-center py-16 bg-white/40 backdrop-blur-xs rounded-3xl border border-white/60 max-w-sm mx-auto">
            <p className="text-sm font-semibold text-slate-700">Sin resultados</p>
            <p className="text-xs text-slate-400 mt-1">No se encontró ninguna aplicación.</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-y-8 gap-x-4 sm:gap-x-6 justify-items-center">
            {filteredApps.map((app) => {
              const IconComp = app.icon;
              return (
                <Link
                  key={app.id}
                  href={app.href}
                  onClick={() => {
                    if (app.workplace && setSelectedWorkplace) {
                      setSelectedWorkplace(app.workplace);
                    }
                    handleClose();
                  }}
                  title={app.name}
                  className="group flex flex-col items-center gap-2 p-2 rounded-2xl transition-all duration-150 cursor-pointer w-28 text-center"
                >
                  {/* Icono Vectorial Abstracto Original con Elevación Suave */}
                  <div className="relative w-16 h-16 sm:w-18 sm:h-18 transition-transform duration-200 group-hover:scale-105 group-hover:-translate-y-1">
                    <IconComp size={64} className="w-full h-full select-none" />
                  </div>

                  {/* Título del Módulo en Tipografía Nítida */}
                  <span className="text-[12px] sm:text-[13px] font-medium text-slate-700 group-hover:text-blue-600 transition-colors tracking-tight line-clamp-1" title={app.name}>
                    {app.name}
                  </span>
                </Link>
              );
            })}
          </div>
        )}
      </main>

            {/* Footer: firma de la plataforma */}
            <div className="sticky bottom-0 z-20 w-full border-t border-white/70 bg-white/60 backdrop-blur-md">
              <footer className="w-full max-w-4xl mx-auto px-6 py-3 flex items-center justify-between gap-4 text-[11px] text-slate-400">
                <div className="flex items-center gap-2">
                  <img
                    src="/brand/rege-symbol.svg"
                    alt=""
                    aria-hidden="true"
                    draggable={false}
                    className="h-3.5 w-auto opacity-70"
                  />
                  <span className="font-bold text-slate-500">REGE</span>
                  <span className="hidden sm:inline">· Real Estate Growth Engine</span>
                  <span className="hidden text-slate-300 md:inline">|</span>
                  <span className="hidden md:inline">regeproptech.com</span>
                </div>
                {!isStandalone && (
                  <span>Presiona <kbd className="bg-white/70 px-1 py-0.5 rounded border border-white/80 font-mono text-slate-500">Esc</kbd> para salir</span>
                )}
              </footer>
            </div>
    </div>
  );
}
